import type { HTMLAttributes, ReactNode } from 'react';
import type { Ref } from 'react';
import { Prompt, PromptActions } from './Prompt';
import { Confirmation } from './Confirmation';
import type { ConfirmationTone } from './Confirmation';
import { Button } from './Button';
import { ModalHead } from './ModalHead';

export interface AskDialogProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'children'> {
  tone: ConfirmationTone;
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  busy?: boolean;
  confirmLabel: ReactNode;
  cancelLabel: ReactNode;
  dismissLabel?: string;
  cornerDismiss?: boolean;
  danger?: boolean;
  cardClassName?: string;
  onDecide: (confirmed: boolean) => void;
  onDismiss?: () => void;
}

export function AskDialog(
  { ref, tone,
    icon,
    title,
    children,
    confirmLabel,
    cancelLabel,
    dismissLabel = 'Schließen',
    cornerDismiss = false,
    headingLevel,
    busy = false,
    danger = false,
    onDecide,
    onDismiss,
    className,
    cardClassName,
    ...rest
  }: AskDialogProps & { ref?: Ref<HTMLDivElement> },
) {
  const cancelString = typeof cancelLabel === 'string' ? cancelLabel : undefined;
  return (
    <Prompt
      ref={ref}
      className={className}
      cardClassName={cardClassName}
      cardRole="alertdialog"
      aria-label={typeof title === 'string' ? title : undefined}
      busy={busy}
      onEscape={onDismiss ?? (() => onDecide(false))}
      topbar={
        <ModalHead closeLabel={cornerDismiss ? cancelString ?? dismissLabel : dismissLabel} closeDisabled={busy} onClose={onDismiss ?? (() => onDecide(false))} />
      }
      {...rest}
    >
      <Confirmation tone={tone} icon={icon} title={title} headingLevel={headingLevel}>
        {children}
      </Confirmation>
      <PromptActions>
        {!cornerDismiss && (
          <Button disabled={busy} onClick={() => onDecide(false)}>
            {cancelLabel}
          </Button>
        )}
        <Button variant={danger ? 'danger' : 'primary'} loading={busy} onClick={() => onDecide(true)}>
          {confirmLabel}
        </Button>
      </PromptActions>
    </Prompt>
  );
}
